import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import {
  Channel,
  deriveWaba,
  extractBackendErrorMessage,
  getBackendErrorStatus,
  getMetaPreview,
  MetaPreview,
  postHsmLine,
} from '@/services/hsm-backend';

export interface LineSetupModalMetaProps {
  channel: Channel;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Meta line setup: confirmation with no inputs. On open it reads the channel
 * data from Kori (`getMetaPreview`: wabaId/fbPageAccessToken/externalId) and
 * displays it. If the only missing datum is the `wabaId` (token + externalId
 * present), it is **derived automatically** on open (roadmap#1435) so the alta
 * obtains and persists it as part of its own flow, instead of depending on a
 * prior onboarding step or a manual click. If Kori data is otherwise
 * incomplete (`complete:false`, e.g. a missing externalId) the "Confirmar alta"
 * button stays blocked. Submit calls `postHsmLine({})`.
 *
 * `fbPageAccessToken` is shown **masked** — it is a token with send capability
 * and must not be left in plain text in the DOM/screenshots.
 */
export const LineSetupModalMeta = ({
  channel,
  onClose,
  onSuccess,
}: LineSetupModalMetaProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [preview, setPreview] = useState<MetaPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deriving, setDeriving] = useState(false);
  // Deriva el wabaId como mucho una vez por apertura del modal: si la
  // derivación automática falla, no se reintenta en loop (queda el botón manual).
  const autoDeriveAttempted = useRef(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setPreview(await getMetaPreview(channel.channelId));
    } catch (error) {
      setLoadError(extractBackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [channel.channelId]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await postHsmLine(channel.channelId, {});
      onSuccess();
    } catch (error) {
      const status = getBackendErrorStatus(error);

      enqueueSnackbar(
        status === 409
          ? 'Esta línea ya está dada de alta'
          : `No se pudo dar de alta la línea: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Onboarding parcial: falta SOLO el wabaId pero hay token + externalId → se
  // puede derivar automáticamente (hsm-backend proxea a Kori, roadmap#1263) en
  // vez de mandar a "completar en Kori a mano". Se dispara solo al abrir el
  // modal (ver el efecto de auto-derivación) y también queda como botón manual.
  const handleDeriveWaba = useCallback(async () => {
    setDeriving(true);
    try {
      await deriveWaba(channel.channelId);
      enqueueSnackbar('wabaId derivado y guardado en Kori', {
        variant: 'success',
      });
      await loadPreview();
    } catch (error) {
      enqueueSnackbar(
        `No se pudo derivar el wabaId: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    } finally {
      setDeriving(false);
    }
  }, [channel.channelId, enqueueSnackbar, loadPreview]);

  const incomplete = preview !== null && !preview.complete;
  const missingFields = preview
    ? (
        [
          ['wabaId', preview.wabaId],
          ['fbPageAccessToken', preview.fbPageAccessToken],
          ['externalId', preview.externalId],
        ] as const
      )
        .filter(([, value]) => !value)
        .map(([key]) => key)
    : [];

  // El único dato faltante es el wabaId (token + externalId presentes) → derivable.
  const canDeriveWaba =
    incomplete && missingFields.length === 1 && missingFields[0] === 'wabaId';

  // roadmap#1435: cuando el único faltante es el wabaId, lo derivamos solo al
  // abrir el modal —sin exigir el click— para que el alta obtenga y guarde el
  // wabaId como parte de su propio flujo. El ref evita reintentar si falla.
  useEffect(() => {
    if (canDeriveWaba && !autoDeriveAttempted.current) {
      autoDeriveAttempted.current = true;
      void handleDeriveWaba();
    }
  }, [canDeriveWaba, handleDeriveWaba]);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Confirmar alta de línea Meta
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ fontFamily: 'ui-monospace, monospace' }}
        >
          {channel.name} · {channel.channelId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress aria-label="loading" />
          </Box>
        )}

        {loadError && (
          <Stack spacing={1.5} alignItems="flex-start">
            <Alert severity="error" sx={{ width: '100%' }}>
              {loadError}
            </Alert>
            <Button
              variant="outlined"
              size="small"
              onClick={() => void loadPreview()}
            >
              Reintentar
            </Button>
          </Stack>
        )}

        {preview && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                columnGap: 1.5,
                rowGap: 1,
                fontFamily: 'ui-monospace, monospace',
                fontSize: 13,
              }}
            >
              <span>wabaId</span>
              <MetaValue value={preview.wabaId} />
              <span>fbPageAccessToken</span>
              <MetaValue value={preview.fbPageAccessToken} mask />
              <span>externalId</span>
              <MetaValue value={preview.externalId} />
            </Box>

            {incomplete ? (
              <Stack
                spacing={1.5}
                alignItems="flex-start"
                sx={{ width: '100%' }}
              >
                <Alert severity="warning" sx={{ width: '100%' }}>
                  {missingFields.length === 1 ? 'Falta' : 'Faltan'} datos en
                  Kori (<strong>{missingFields.join(', ')}</strong>) para dar de
                  alta esta línea.{' '}
                  {canDeriveWaba
                    ? 'El wabaId se deriva automáticamente desde el token; si no, usá el botón.'
                    : 'Reconectá el canal de Meta en el onboarding para completar estos datos y volvé a intentar.'}
                </Alert>
                {canDeriveWaba && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void handleDeriveWaba()}
                    disabled={deriving || submitting}
                    startIcon={
                      deriving ? <CircularProgress size={16} /> : undefined
                    }
                  >
                    {deriving ? 'Derivando…' : 'Derivar wabaId automáticamente'}
                  </Button>
                )}
              </Stack>
            ) : (
              <Alert severity="info">
                Los datos se leen de Kori. Sin inputs manuales — solo confirmás
                el alta.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={
            loading ||
            incomplete ||
            Boolean(loadError) ||
            submitting ||
            deriving
          }
        >
          Confirmar alta
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Masks a long secret: `EAAJ…gZD`. Short values → bullets. */
function maskSecret(value: string): string {
  if (value.length <= 7) {
    return '••••';
  }

  return `${value.slice(0, 4)}…${value.slice(-3)}`;
}

/**
 * Renders a Kori field value, or a red "Falta en Kori" when missing. With
 * `mask`, long secrets (the fbPageAccessToken) are shown truncated.
 */
const MetaValue = ({
  value,
  mask = false,
}: {
  value: string | null;
  mask?: boolean;
}) => {
  if (!value) {
    return (
      <Typography component="strong" color="error" sx={{ fontSize: 13 }}>
        Falta en Kori
      </Typography>
    );
  }

  return <strong>{mask ? maskSecret(value) : value}</strong>;
};
